import { DestroyRef, Injectable, OnDestroy } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { components, OPTIONS } from 'src/app/shared/const/globals.const';
import { SurgeonOpeningsDal } from 'src/app/shared/dal/surgeon-openings.dal';
import { roomWithDoctorAgendas } from 'src/app/shared/interfaces/room.interfaces';
import { Profile } from 'src/app/shared/models/profile.model';
import { Specialty } from 'src/app/shared/models/specialty.model';
import { ErrorService } from 'src/app/shared/services/error.service';
import { PythonAPIService } from 'src/app/shared/services/pythonApi.service';
import { RoomService } from 'src/app/shared/services/room.service';
import { UserService } from 'src/app/shared/services/user.service';
import { UserConfigurationService } from 'src/app/shared/services/userConfiguration.service';
import { UtilisService } from 'src/app/shared/services/utilis.service';
import {
  formatDateTime,
  getSurgeonSpecialtiesBySelectedHospitals,
  initIsDifferentSurgeonsBetweenSurgeonOpeningsAndDoctorAgendas,
  setTypeAttributeArray,
  sortRoomsByPriority,
} from 'src/app/shared/utils/cross-functions';
import { getFirstHospitalSelectedData } from '../../../app/shared/utils/cross-functions';
import { setOverflowDatasColors } from '../../shared/utils/cross-functions';
import { OperatingScheduleService } from '../operatingschedule.service';
import { posthog } from 'posthog-js';

@Injectable({
  providedIn: 'root',
})
export class SurgeonOpeningsService implements OnDestroy {
  private instanceId: string;

  public refreshAveragesSubject: Subject<void> = new Subject();

  public setNotifyAnesthSubject: Subject<void> = new Subject();

  private filterTriggerSubject: Subject<void> = new Subject<void>();

  public perimeterSelectorSubject: Subject<void> = new Subject<void>();

  private closeRoomTriggerSubject: Subject<void> = new Subject<void>();

  private importSOTriggerSubject: Subject<string> = new Subject<string>();

  private generateSOFromDATriggerSubject: Subject<void> = new Subject<void>();

  private filterAnesthTriggerSubject: Subject<void> = new Subject<void>();

  private importDATriggerSubject: Subject<string> = new Subject<string>();

  private isRealizedModeTriggerSubject: Subject<[boolean, boolean]> = new Subject<[boolean, boolean]>();

  private filterInterventionsTriggerSubject: BehaviorSubject<{
    types: string[];
    rooms: string[];
    specialties: string[];
    number: number;
  }> = new BehaviorSubject<{ types: string[]; rooms: string[]; specialties: string[]; number: number }>({
    types: [''],
    rooms: [],
    specialties: [],
    number: 0,
  });

  private interventionTypeTriggerSubject: BehaviorSubject<string> = new BehaviorSubject<string>('1');

  public filterOptions: Subject<any> = new Subject<any>();

  public day: string | undefined;

  public isoDate: string | undefined;

  public selectedRoomIndex: number = 0;

  public rooms: any[] | undefined;

  public roomsSurgeonOpeningsAllData: any[] | undefined;

  public filtredSpecialties: (Specialty & { checked?: boolean })[] = [];

  public isInfoLoading: boolean = true;

  public typeAttributeArray: string[] | undefined;

  private roomsOverflowData: any[] | undefined;

  public lastUpdateDate: string | undefined; // unused

  public lastUpdateMessage: Subject<string> = new Subject<string>();

  private _roomsSurgeonOpenings$ = new BehaviorSubject<any[] | undefined>([]);

  private roomsAllData$: BehaviorSubject<roomWithDoctorAgendas[]> = new BehaviorSubject<roomWithDoctorAgendas[]>([]);

  public roomsAllData = this.roomsAllData$.asObservable();

  public roomsSurgeonOpenings$ = this._roomsSurgeonOpenings$.asObservable();

  public filter = this.filterTriggerSubject.asObservable();

  public closeRoom = this.closeRoomTriggerSubject.asObservable();

  public importSO = this.importSOTriggerSubject.asObservable();

  public generateSOFromDA = this.generateSOFromDATriggerSubject.asObservable();

  public filterAnesth = this.filterAnesthTriggerSubject.asObservable();

  public importDA = this.importDATriggerSubject.asObservable();

  public isRealized = this.isRealizedModeTriggerSubject.asObservable();

  public filterInterventions = this.filterInterventionsTriggerSubject.asObservable();

  public interventionType = this.interventionTypeTriggerSubject.asObservable();

  public populationList: any[] = [];

  public showOnlyOpenRooms: boolean = false;

  constructor(
    private utilisService: UtilisService,
    private operatingScheduleService: OperatingScheduleService,
    private pythonApisService: PythonAPIService,
    private roomsService: RoomService,
    private errorService: ErrorService,
    private userService: UserService,
    private surgeonOpeningsDal: SurgeonOpeningsDal,
    private userConfigurationService: UserConfigurationService,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  initialization(dateArg?: string, noOverflow?: boolean): void {
    this.filtredSpecialties = [];
    let date;
    if (dateArg) {
      date = new Date(dateArg);
    } else {
      date = new Date(this.utilisService.getDate(components.SURGEON_ROOM_COMPONENT)?.format('YYYY-MM-DD') ?? '');
    }
    this.isoDate = date.toISOString();
    this.day = this.isoDate;
    this.selectedRoomIndex = 0;
    this.initPopulation();
    this.fetchData(true, true, true, noOverflow);
  }

  initPopulation(): void {
    this.subscriptionManager
      .createRequest(this.userConfigurationService.getUserConfigurations(), 'getUserConfigurations', this.instanceId)
      .subscribe({
        next: (userConfiguration: any) => {
          this.populationList = userConfiguration.population
            ? userConfiguration.population.sort((a: any, b: any) => a.priority - b.priority)
            : [];
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  fetchData(
    fetchSurgeonOpenings: boolean,
    fetchDoctorAgendas: boolean,
    triggerActions: boolean,
    noOverflow?: boolean
  ): Promise<void> {
    const latestPopSelected = localStorage.getItem('latestPopSelected');
    const population = this.populationList.find((pop: any) => pop._id === latestPopSelected);
    return new Promise((resolve: any, reject: any) => {
      const promises: any[] = [];
      if (fetchSurgeonOpenings) {
        promises.push(this.getHospitalRoomsDaysSurgeonOpenings());
        // promises.push(this.getLastUpdateDateSO());
      }

      if (fetchDoctorAgendas) {
        promises.push(this.getHospitalRoomsDaysDoctorAgendas());
      }

      Promise.all(promises)
        .then(() => {
          // Sort data
          if (fetchSurgeonOpenings) {
            sortRoomsByPriority(this.roomsSurgeonOpeningsAllData, false);
          }

          if (fetchDoctorAgendas) {
            if (fetchSurgeonOpenings) {
              // Sort roomsAllData based on this.roomsSurgeonOpeningsAllData
              const tmp: any[] = [];
              for (const item of this.roomsSurgeonOpeningsAllData ?? []) {
                const roomId = String(item.room._id);
                const found = this.getRoomsAllDataValue()?.find((elt: any) => String(elt.room._id) === roomId);
                tmp.push(found);
              }
              this.setRoomsAllData(tmp);
            } else {
              sortRoomsByPriority(this.getRoomsAllDataValue(), false);
            }
          }

          this.operatingScheduleService.isSavingSurgeonOpenings = false;

          this.filterBySpecialty();

          if (triggerActions) {
            this.triggerSetNotifyAnesth();
            this.triggerRefreshAverages();
          }

          // We resolve here bacause we have enough data to show to the user
          this.isInfoLoading = false;
          this.filterRoomsForPopulation(population);
          resolve();

          if (!noOverflow) {
            // I commented this line because i suspected that is related to a CORS error that we were having in production
            // this.getRoomsOverflowData();
          }

          this.initIsDifferentSurgeons();
          this.setLastUpdateMessage();
        })
        .catch((e: any) => {
          reject(e);
        });
    });
  }

  filterRoomsForPopulation(population?: any) {
    if (population) {
      if (population.roomsPerimeter && population.roomsPerimeter.length > 0) {
        this.roomsSurgeonOpenings = this.roomsSurgeonOpeningsAllData?.filter((room: any) =>
          population.roomsPerimeter.includes(room.room._id)
        );
      }
    } else {
      this.roomsSurgeonOpenings = this.roomsSurgeonOpeningsAllData;
    }
  }

  // lastUpdateDate retrieved from setLastUpdateMessage + variable unsused

  // getLastUpdateDateSO(): Promise<void> {
  //   return new Promise((resolve, reject) => {
  //     if (this.getLastUpdateDateSubscription) {
  //       this.getLastUpdateDateSubscription.unsubscribe();
  //     }

  //     const date = moment(this.isoDate).clone().utc().format("YYYY-MM-DD");
  //     this.getLastUpdateDateSubscription = this.getLastUpdateDate(date).subscribe((result) => {
  //       this.lastUpdateDate = null;
  //       if (result.length != 0) {
  //         this.lastUpdateDate = result[0].lastUpdateDate ? formatDateTime(result[0].lastUpdateDate) : null
  //       }
  //       resolve();
  //     }, error => {
  //       this.errorService.handleError(error)
  //       reject();
  //     });
  //   });
  // }

  initIsDifferentSurgeons(): void {
    initIsDifferentSurgeonsBetweenSurgeonOpeningsAndDoctorAgendas(
      this.getRoomsAllDataValue() ?? [],
      this.roomsSurgeonOpeningsAllData ?? []
    );
  }

  // Dans surgeonopening.service.ts
  filterBySpecialty(): void {
    this.perimeterSelectorSubject.next();
    const filtredSpecialtiesIds = this.filtredSpecialties.map((s: any) => String(s._id));
    const indexesToKeep: number[] = [];

    // Filtrage par spécialité (comportement original)
    if (filtredSpecialtiesIds.length > 0) {
      this.filterRoomsForPopulation();
      this.roomsSurgeonOpenings = this.roomsSurgeonOpeningsAllData?.filter((elt: any, index: number) => {
        const include = elt.surgeonopening
          .filter((SO: any) => SO.opening === true)
          .some((SO: any) => {
            const surgeonSpecialties = getSurgeonSpecialtiesBySelectedHospitals(
              SO.surgeon,
              this.userService.getSelectedHospitals()
            ).map((specialty: any) => String(specialty._id));
            let included = false;
            surgeonSpecialties.forEach((specialty: any) => {
              if (filtredSpecialtiesIds.includes(specialty)) {
                included = true;
              }
            });
            return included;
          });

        if (include) {
          indexesToKeep.push(index);
        }

        return include;
      });

      this.rooms = this.getRoomsAllDataValue()?.filter((_elt: any, index: number) => indexesToKeep.includes(index));
    }
    // Filtrage uniquement par salles ouvertes (si activé)
    else if (this.showOnlyOpenRooms) {
      this.filterRoomsForPopulation();
      this.roomsSurgeonOpenings = this.roomsSurgeonOpeningsAllData?.filter((elt: any) => {
        return elt.surgeonopening.some((SO: any) => SO.opening === true);
      });

      // Pour maintenir l'alignement avec roomsAllData
      const openRoomIds = this.roomsSurgeonOpenings?.map((room: any) => String(room.room._id));
      this.rooms = this.getRoomsAllDataValue()?.filter((roomData: any) =>
        openRoomIds?.includes(String(roomData.room._id))
      );
    }
    // Aucun filtre
    else {
      this.rooms = this.getRoomsAllDataValue();
      this.roomsSurgeonOpenings = this.roomsSurgeonOpeningsAllData;
    }
  }

  public set roomsSurgeonOpenings(data: any[] | undefined) {
    this._roomsSurgeonOpenings$.next(data);
  }

  public get roomsSurgeonOpenings(): any[] | undefined {
    return this._roomsSurgeonOpenings$.getValue();
  }

  getHospitalRoomsDaysDoctorAgendas(): Promise<void> {
    return new Promise((resolve: any, reject: any) => {
      this.rooms = [];
      this.setRoomsAllData([]);
      this.subscriptionManager
        .createRequest(
          this.roomsService.getHospitalRoomsDayDoctorAgendas(this.isoDate!),
          'getHospitalRoomsDayDoctorAgendas',
          this.instanceId
        )
        .subscribe({
          next: (rooms: any) => {
            this.setRoomsAllData(rooms);
            resolve();
          },
          error: (error: any) => {
            this.errorService.handleError(error);
            reject(error);
          },
        });
    });
  }

  setRoomsAllData(data: roomWithDoctorAgendas[]) {
    this.roomsAllData$.next(data);
  }

  getRoomsAllDataValue(): roomWithDoctorAgendas[] {
    return this.roomsAllData$.value;
  }

  triggerRefreshAverages(): void {
    this.refreshAveragesSubject.next();
  }

  getHospitalRoomsDaysSurgeonOpenings(): Promise<void> {
    return new Promise((resolve: any, reject: any) => {
      const date = moment(this.isoDate).clone().utc().format('YYYY-MM-DD');
      this.roomsSurgeonOpenings = [];
      this.roomsSurgeonOpeningsAllData = [];
      this.subscriptionManager
        .createRequest(
          this.roomsService.getHospitalRoomsDaysSurgeonOpenings(date),
          'getHospitalRoomsDaysSurgeonOpenings',
          this.instanceId
        )
        .subscribe({
          next: (rooms: any) => {
            this.roomsSurgeonOpeningsAllData = rooms;
            this.typeAttributeArray = setTypeAttributeArray(this.roomsSurgeonOpeningsAllData ?? [], false);
            resolve();
          },
          error: (error: any) => {
            this.errorService.handleError(error);
            reject(error);
          },
        });
    });
  }

  triggerSetNotifyAnesth(): void {
    this.setNotifyAnesthSubject.next();
  }

  getRoomsOverflowData(): void {
    this.roomsOverflowData = [];
    this.subscriptionManager
      .createRequest(
        this.pythonApisService.getRoomsOverflowData(this.isoDate!),
        'getRoomsOverflowData',
        this.instanceId
      )
      .subscribe({
        next: (data: any) => {
          this.roomsOverflowData = data;
          this.roomsOverflowData?.forEach((r1: any) => {
            if (this.roomsSurgeonOpeningsAllData) {
              const surgeonOpening = this.roomsSurgeonOpeningsAllData.find(
                (r2: any) => String(r1.room._id) === String(r2.room._id)
              );
              if (surgeonOpening) {
                surgeonOpening.overflowDuration = r1.overflowDuration;
                setOverflowDatasColors(surgeonOpening);
              }
            }

            if (this.roomsAllData$) {
              const doctorAgenda: any = this.getRoomsAllDataValue()?.find(
                (r2: any) => String(r1.room._id) === String(r2.room._id)
              );
              if (doctorAgenda) {
                doctorAgenda.overflowDuration = r1.overflowDuration;
                setOverflowDatasColors(doctorAgenda);
              }
            }
          });
        },

        error: (error: any) => {
          this.errorService.handleError(error);
        },
      });
  }

  getFromDataExtraction(fromDate: string, toDate: string): Observable<any> {
    return this.surgeonOpeningsDal.getFromDataExtraction(fromDate, toDate);
  }

  getLastUpdateDate(date: string) {
    return this.surgeonOpeningsDal.getLastUpdateDate(date);
  }

  cancelHttpRequests(): void {
    this.subscriptionManager.cancelRequestsForComponent(this.instanceId);
  }

  setLastUpdateMessage(): void {
    const mostRecentDoc = this.getMostRecentDoc();
    let lastUpdateUser: Profile;

    if (!mostRecentDoc) {
      this.lastUpdateMessage.next('');
      return;
    }

    const lastUpdateDate = mostRecentDoc.updatedAt;
    this.lastUpdateDate = formatDateTime(lastUpdateDate);

    const arr = moment(lastUpdateDate).format('YYYY-MM-DD').split('-');
    const updateDate = arr[2] + '/' + arr[1] + '/' + arr[0];
    const updateHour = moment(lastUpdateDate).format('HH:mm');

    if (mostRecentDoc.updatedBy && typeof mostRecentDoc.updatedBy === 'object') {
      lastUpdateUser = mostRecentDoc.updatedBy;

      this.lastUpdateMessage.next(
        `Modifié le ${updateDate} à ${updateHour}\nPar ${lastUpdateUser.firstName} ${lastUpdateUser.lastName}\n`
      );
    } else if (mostRecentDoc.scrapedData) {
      const hospital = getFirstHospitalSelectedData(
        this.userService.getCurrentUser(),
        this.userService.getSelectedHospitals()
      );
      const ProgramsDataPipelineOption = hospital?.options.find((e: any) => {
        return e.name === OPTIONS.PROGRAMS_DATA_PIPELINE;
      });
      const softwareName =
        ProgramsDataPipelineOption && ProgramsDataPipelineOption.softwareName
          ? ProgramsDataPipelineOption.softwareName
          : '';

      this.lastUpdateMessage.next(`Importé le ${updateDate} à ${updateHour}\nDepuis ${softwareName}\n`);
    } else {
      this.lastUpdateMessage.next('');
    }
  }

  getMostRecentDoc(): any {
    let mostRecentDoc: any = null;
    let mostRecentDate: Date | null = null;
    const updatedData: any[] = [];

    this.roomsSurgeonOpeningsAllData?.forEach((e: any) => {
      if (e.surgeonopening.length > 0) {
        e.surgeonopening.forEach((e: any) => updatedData.push(e));
      }
    });

    if (updatedData) {
      updatedData.forEach((e: any) => {
        const currentUpdatedAt = new Date(e.updatedAt);
        if (!mostRecentDate || currentUpdatedAt > mostRecentDate) {
          mostRecentDate = currentUpdatedAt;
          mostRecentDoc = e;
        }
      });
    }

    return mostRecentDoc;
  }

  // Per Vacations
  triggerImportSO(softwareName: string) {
    this.importSOTriggerSubject.next(softwareName);
  }

  triggerGenerateSOFromDA() {
    this.generateSOFromDATriggerSubject.next();
  }

  triggerFilterPerVacation() {
    this.filterTriggerSubject.next();
  }

  triggerCloseRoomsPerVacation() {
    this.closeRoomTriggerSubject.next();
  }

  // Anesth Needs
  triggerFilterAnesthNeeds() {
    this.filterAnesthTriggerSubject.next();
  }

  triggerFilterInterventions(filterValues: {
    types: string[];
    rooms: string[];
    specialties: string[];
    number: number;
  }) {
    this.filterInterventionsTriggerSubject.next(filterValues);
  }

  getTriggerFilterInterventions() {
    return this.filterInterventionsTriggerSubject.value;
  }

  triggerInterventionType(type: string) {
    posthog.capture('Switch_scheduled/real_Interventions', { page_source: 'Interventions_By_Rooms' });
    this.interventionTypeTriggerSubject.next(type);
  }

  //Per intervention
  triggerImportDA(value: any) {
    this.importDATriggerSubject.next(value);
  }

  triggerRealizedMode(isRealizedMode: boolean, isSelectedDayPast: boolean) {
    this.isRealizedModeTriggerSubject.next([isRealizedMode, isSelectedDayPast]);
    if (!isSelectedDayPast) {
      this.triggerInterventionType('1');
    }
  }

  clear(): void {
    this.subscriptionManager.cancelRequestsForComponent(this.instanceId);
    this.refreshAveragesSubject.complete();
    this.refreshAveragesSubject = new Subject<void>();
    this.setNotifyAnesthSubject.complete();
    this.setNotifyAnesthSubject = new Subject<void>();
    this.filterTriggerSubject.complete();
    this.filterTriggerSubject = new Subject<void>();
    this.perimeterSelectorSubject.complete();
    this.perimeterSelectorSubject = new Subject<void>();
    this.closeRoomTriggerSubject.complete();
    this.closeRoomTriggerSubject = new Subject<void>();
    this.importSOTriggerSubject.complete();
    this.importSOTriggerSubject = new Subject<string>();
    this.generateSOFromDATriggerSubject.complete();
    this.generateSOFromDATriggerSubject = new Subject<void>();
    this.filterAnesthTriggerSubject.complete();
    this.filterAnesthTriggerSubject = new Subject<void>();
    this.importDATriggerSubject.complete();
    this.importDATriggerSubject = new Subject<string>();
    this.isRealizedModeTriggerSubject.complete();
    this.isRealizedModeTriggerSubject = new Subject<[boolean, boolean]>();
    this.interventionTypeTriggerSubject.complete();
    this.interventionTypeTriggerSubject = new BehaviorSubject<string>('1');
    this.filterInterventionsTriggerSubject.complete();
    this.filterInterventionsTriggerSubject = new BehaviorSubject<{
      types: string[];
      rooms: string[];
      specialties: string[];
      number: number;
    }>({
      types: [''],
      rooms: [],
      specialties: [],
      number: 0,
    });
    this.filterOptions.complete();
    this.filterOptions = new Subject<any>();
    this.lastUpdateMessage.complete();
    this.lastUpdateMessage = new Subject<string>();
    this._roomsSurgeonOpenings$.complete();
    this._roomsSurgeonOpenings$ = new BehaviorSubject<any[] | undefined>([]);
    this.roomsAllData$.complete();
    this.roomsAllData$ = new BehaviorSubject<roomWithDoctorAgendas[]>([]);

    this.filter = this.filterTriggerSubject.asObservable();
    this.roomsAllData = this.roomsAllData$.asObservable();
    this.closeRoom = this.closeRoomTriggerSubject.asObservable();
    this.importSO = this.importSOTriggerSubject.asObservable();
    this.generateSOFromDA = this.generateSOFromDATriggerSubject.asObservable();
    this.filterAnesth = this.filterAnesthTriggerSubject.asObservable();
    this.importDA = this.importDATriggerSubject.asObservable();
    this.isRealized = this.isRealizedModeTriggerSubject.asObservable();
    this.filterInterventions = this.filterInterventionsTriggerSubject.asObservable();
    this.roomsSurgeonOpenings$ = this._roomsSurgeonOpenings$.asObservable();
    this.interventionType = this.interventionTypeTriggerSubject.asObservable();

    this.day = undefined;
    this.isoDate = undefined;
    this.selectedRoomIndex = 0;
    this.rooms = undefined;
    this.roomsSurgeonOpeningsAllData = undefined;
    this.filtredSpecialties = [];
    this.isInfoLoading = true;
    this.typeAttributeArray = undefined;
    this.roomsOverflowData = undefined;
    this.lastUpdateDate = undefined;
    this.populationList = [];
    this.showOnlyOpenRooms = false;
  }

  ngOnDestroy() {
    this.importSOTriggerSubject.complete();
    this.generateSOFromDATriggerSubject.complete();
    this.filterTriggerSubject.complete();
    this.closeRoomTriggerSubject.complete();
    this.filterAnesthTriggerSubject.complete();
    this.roomsAllData$.complete();
  }
}
